/**
 * Org Settings → Danger.
 *
 * Two destructive actions:
 *   1. Transfer ownership — typed-name confirm (irreversible; new owner
 *      gets the keys, current owner is demoted to admin)
 *   2. Soft-delete org — simple confirm (recoverable for 30 days via
 *      the recovery page in Phase 5c-2)
 *
 * Visibility:
 *   - Soft-delete: owner only.
 *   - Transfer ownership: owner only. Picks any existing org member.
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button, Select, Label } from '@/components/ui'
import type { OrgWithRole, MemberWithUser } from '@/lib/api'
import { orgsAPI } from '@/lib/api'
import { authClient } from '@/auth/authClient'
import { useIdentity } from '@/hooks/useIdentity'
import { useCanI } from '@/hooks/useCanI'
import { ConfirmDialog, TypedConfirmDialog } from '@/components/ConfirmDialog'

export function OrgDanger({ org, onDeleted }: { org: OrgWithRole; onDeleted: () => Promise<void> }) {
  const { state, refreshIdentity } = useIdentity()
  const navigate = useNavigate()
  const myUserId = state.status === 'ready' ? state.identity.user.id : ''
  // #2510: the recovery hold length (0 = permanent, no hold) and whether
  // this is the user's last org (so deleting it leaves nowhere to land →
  // sign out). A secondary-org delete keeps the session and lands them in
  // a remaining org.
  const recoveryWindowDays =
    (state.status === 'ready' ? state.identity.recoveryWindowDays : 0) ?? 0
  const permanent = recoveryWindowDays === 0
  const isLastOrg =
    (state.status === 'ready' ? state.identity.orgs.length : 1) <= 1
  // Both danger actions are gated through the matrix. Transfer is
  // owner-only via PERMISSIONS.org.transferOwnership; soft-delete is
  // owner-only via PERMISSIONS.org.delete. Today they coincide on
  // owner, but reading them separately means a future split (e.g.
  // delegating delete to admin) would only need a matrix change —
  // this UI is already wired to ask the right question.
  const canTransfer = useCanI('org.transferOwnership', org._id)
  const canDelete = useCanI('org.delete', org._id)
  const canPerformDangerActions = canTransfer || canDelete

  const [members, setMembers] = useState<MemberWithUser[]>([])
  const [transferTarget, setTransferTarget] = useState<string>('')
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    // Only need the member list for the transfer flow's recipient
    // dropdown; skip the fetch when transfer isn't available.
    if (!canTransfer) return
    void (async () => {
      try {
        const result = await orgsAPI.listMembers(org._id)
        setMembers(result.members ?? [])
      } catch {
        // Surface a soft warning later if we actually try to transfer
      }
    })()
  }, [org._id, canTransfer])

  if (!canPerformDangerActions) {
    return (
      <p className="text-sm text-muted-foreground">
        You don't have permission to perform destructive actions in this organization.
      </p>
    )
  }

  // Eligible transfer recipients: any non-owner member.
  const eligible = members.filter(m => m.role !== 'owner' && m.userId !== myUserId)

  async function handleTransfer() {
    setActionError(null)
    setBusy(true)
    try {
      // Promote the chosen member to owner. Better Auth allows
      // multiple owners; the demotion of the current owner happens
      // as a follow-up step below for "transfer" (not "co-own")
      // semantics.
      await orgsAPI.changeMemberRole(org._id, transferTarget, 'owner')
      // Demote self to admin so the org has exactly one owner again.
      const myMember = members.find(m => m.userId === myUserId)
      if (myMember) {
        await orgsAPI.changeMemberRole(org._id, myMember.id, 'admin')
      }
      await refreshIdentity()
      setShowTransferConfirm(false)
      // Stay on the page — UI rerenders with the user as admin and
      // hides the danger-zone controls accordingly.
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Transfer failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleSoftDelete() {
    setActionError(null)
    setBusy(true)
    try {
      await orgsAPI.softDelete(org._id)
      if (isLastOrg) {
        // No org left to land in — sign out and return to login. When a
        // hold applies, the next login offers a one-click restore (#2510).
        await authClient.signOut()
        navigate('/auth/login', { replace: true })
      } else {
        // Still a member of another org — stay signed in and let normal
        // routing drop the user into a remaining workspace.
        await onDeleted()
        navigate('/', { replace: true })
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
      setBusy(false)
    }
  }

  return (
    <div className="space-y-8 max-w-md">
      {/* ── Transfer ownership ─────────────────────────── */}
      {canTransfer && (
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Transfer ownership</h2>
          <p className="text-sm text-muted-foreground">
            Promote another member to owner. You'll be demoted to admin.
            This is irreversible without the new owner's cooperation.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="transfer-target">New owner</Label>
          <Select
            id="transfer-target"
            value={transferTarget}
            onChange={(e) => setTransferTarget(e.target.value)}
            disabled={eligible.length === 0}
            placeholder="— Select a member —"
            options={eligible.map(m => ({
              value: m.id,
              label: `${m.user.name || m.user.email} (${m.role})`,
            }))}
          />
          {eligible.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No eligible members. Invite someone first (Phase 5c-2).
            </p>
          )}
        </div>

        <Button
          variant="outline"
          disabled={!transferTarget || busy}
          onClick={() => setShowTransferConfirm(true)}
        >
          Transfer ownership
        </Button>
      </section>
      )}

      {canTransfer && canDelete && <hr className="border-border" />}

      {/* ── Soft-delete ────────────────────────────────── */}
      {canDelete && (
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-destructive">Delete organization</h2>
          <p className="text-sm text-muted-foreground">
            {permanent ? (
              <>
                All members lose access immediately, and workspaces, apps,
                and test data are permanently deleted. This can&apos;t be
                undone.
              </>
            ) : (
              <>
                All members lose access immediately. Workspaces, apps, and
                test data become unreachable. Recoverable for{' '}
                {recoveryWindowDays} days from the recovery page; after that,
                everything is permanently deleted.
              </>
            )}
          </p>
        </div>

        <Button
          variant="destructive"
          disabled={busy}
          onClick={() => setShowDeleteConfirm(true)}
        >
          Delete this organization
        </Button>
        {!permanent && (
          <p className="text-xs text-muted-foreground">
            Deleted by mistake?{' '}
            <Link to="/recover" className="underline hover:text-foreground">
              Recover
            </Link>
            {' '}within the recovery window.
          </p>
        )}
      </section>
      )}

      {actionError && (
        <p className="text-sm text-destructive" role="alert">{actionError}</p>
      )}

      {showTransferConfirm && (
        <TypedConfirmDialog
          open
          onClose={() => setShowTransferConfirm(false)}
          title="Transfer organization ownership"
          description={
            <>
              You're about to make{' '}
              <strong>
                {eligible.find(m => m.id === transferTarget)?.user.name ||
                  eligible.find(m => m.id === transferTarget)?.user.email}
              </strong>
              {' '}the owner of <strong>{org.name}</strong>. You'll be
              demoted to admin and will need their cooperation to
              reverse this.
            </>
          }
          expectedPhrase={org.name}
          confirmLabel="Transfer ownership"
          onConfirm={handleTransfer}
          busy={busy}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDeleteConfirm(false)}
          title="Delete organization"
          description={
            permanent ? (
              <>
                <strong>{org.name}</strong> and all its workspaces, apps, and
                test data will be permanently deleted. This can&apos;t be
                undone.
                {isLastOrg ? " You'll be signed out." : ''}
              </>
            ) : (
              <>
                <strong>{org.name}</strong> and all its workspaces become
                unreachable immediately. You have {recoveryWindowDays} days to
                restore it from the recovery page before it&apos;s permanently
                deleted.
                {isLastOrg
                  ? " Because it's your last organization, you'll be signed out — log back in within that time to restore it."
                  : ''}
              </>
            )
          }
          confirmLabel={permanent ? 'Delete permanently' : 'Delete organization'}
          onConfirm={handleSoftDelete}
          busy={busy}
        />
      )}
    </div>
  )
}
